import { watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { useAdministrationFeatures } from './use-administration-features';
import { authenticatedSettingsLoadPromise } from './use-settings-load-when-authenticated';
import { settingsLoadPromise } from './use-update-settings';
import { useOUserSettingsStore } from '@/stores/use-user-settings-store';
import { ADMIN_FEATURE_FALLBACK_PATH, notifyAdministrationUnavailable } from '@/navguards/administration-features-guard';
import { adminRouteUnavailableReason, isAdministrationPolicyRoute } from '@/utils/administration-features';

/**
 * Re-checks the open page when the administration policy or the signed-in user
 * changes, for example after the settings poll brings a new
 * `administration_disabled` list, or after a role or domain change. An
 * administration page that became unavailable is left for home (`/`) with the
 * same message as the navigation guard. Leaving unmounts the page, which drops
 * its data and stops its requests. Navigation itself is checked by
 * `administrationFeaturesGuard`. Mount it once, in the app root.
 */
export function useAdministrationRedirect() {
  const router = useRouter();
  const route = useRoute();
  const { policy } = useAdministrationFeatures();
  const { oUserSettings } = storeToRefs(useOUserSettingsStore());

  watch(
    [policy, () => oUserSettings.value?.key],
    async () => {
      await router.isReady();
      await settingsLoadPromise;
      await authenticatedSettingsLoadPromise;

      if (!isAdministrationPolicyRoute(route)) {
        return;
      }

      const reason = adminRouteUnavailableReason(route, policy.value);
      if (!reason) {
        return;
      }

      notifyAdministrationUnavailable(reason);
      await router.replace({ path: ADMIN_FEATURE_FALLBACK_PATH });
    },
    { flush: 'post' },
  );
}
